import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { SignupShell } from '@/features/auth/components/SignupShell';
import { SignupStepHeader } from '@/features/auth/components/SignupStepHeader';
import { PasswordChecklist } from '@/features/auth/components/PasswordChecklist';
import { isPasswordValid } from '@/features/auth/constants/passwordRules';
import {
  useCompleteSignupMutation,
  useOnboardingFieldsQuery,
} from '@/features/auth/api/useAuthMutations';
import {
  getSignupSteps,
  hasSignupProfileStep,
} from '@/features/auth/utils/signupSteps';

type PasswordForm = {
  password: string;
  confirmPassword: string;
};

export function CreatePasswordPage() {
  const navigate = useNavigate();
  const completeSignup = useCompleteSignupMutation();
  const role = localStorage.getItem('role');
  const { data: fieldsData } = useOnboardingFieldsQuery(role);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<PasswordForm>({ mode: 'onChange' });

  const password = watch('password') || '';
  const confirmPassword = watch('confirmPassword') || '';
  const passwordsMatch = Boolean(
    password && confirmPassword && password === confirmPassword,
  );
  const canSubmit = passwordsMatch && isPasswordValid(password);

  const hasProfileFields = hasSignupProfileStep(fieldsData);
  const steps = getSignupSteps(hasProfileFields);

  const handleBack = () => {
    if (hasProfileFields) {
      navigate('/user-detail');
    } else {
      navigate('/otp-verify', {
        state: {
          type: 'Phone',
          email: localStorage.getItem('user_email'),
          mobileNumber: localStorage.getItem('user_mobile'),
        },
      });
    }
  };

  const onSubmit = (data: PasswordForm) => {
    completeSignup.mutate({
      password: data.password,
      sessionId: localStorage.getItem('SESSION_ID') || '',
    });
  };

  const inputClass = (hasError?: boolean) =>
    `signup-input box-border h-[44px] w-full min-w-0 rounded-[9px] border bg-white px-3.5 pr-11 text-sm leading-none text-[#0F1817] outline-none transition-[border-color,box-shadow] placeholder:text-[#A9B4B1] ${
      hasError
        ? 'border-red-400'
        : 'border-[#E2E9E7] hover:border-[#C9D3D1]'
    }`;

  return (
    <SignupShell>
      <div className="signup-form-align flex w-full min-w-0 flex-col">
        <SignupStepHeader
          step={steps.password}
          totalSteps={steps.totalSteps}
          title="Create password"
          subtitle="Choose a strong password to secure your account."
        />

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full min-w-0 flex-col gap-1 text-left"
        >
          <div className="mb-3">
            <label className="mb-1.5 block text-[12.5px] font-medium text-[#3E524F]">
              Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className={inputClass(Boolean(errors.password))}
                placeholder="Enter password"
                {...register('password', { required: 'Password is required' })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute top-1/2 right-2.5 -translate-y-1/2 border-none bg-transparent p-1 text-[#7C8B88] hover:text-[#0F1817]"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {!showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password ? (
              <p className="mt-1 text-[11px] text-red-500">
                {errors.password.message}
              </p>
            ) : null}
            {password.length > 0 ? (
              <PasswordChecklist password={password} />
            ) : null}
          </div>

          <div className="mb-4">
            <label className="mb-1.5 block text-[12.5px] font-medium text-[#3E524F]">
              Confirm password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                className={inputClass(Boolean(errors.confirmPassword))}
                placeholder="Re-enter password"
                {...register('confirmPassword', {
                  required: 'Confirm password is required',
                  validate: (value) =>
                    value === password || 'Passwords do not match',
                })}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute top-1/2 right-2.5 -translate-y-1/2 border-none bg-transparent p-1 text-[#7C8B88] hover:text-[#0F1817]"
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
              >
                {!showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.confirmPassword ? (
              <p className="mt-1 text-[11px] text-red-500">
                {errors.confirmPassword.message}
              </p>
            ) : null}
          </div>

          <button
            type="submit"
            disabled={!canSubmit || completeSignup.isPending}
            className="signup-btn flex h-[46px] w-full items-center justify-center rounded-[9px] border-none text-[14.5px] font-semibold text-white transition-all hover:enabled:-translate-y-px disabled:cursor-not-allowed disabled:opacity-45"
          >
            {completeSignup.isPending ? 'Creating…' : 'Create password'}
          </button>
        </form>

        <button
          type="button"
          onClick={handleBack}
          className="mt-5 border-none bg-transparent p-0 text-sm text-[#7C8B88] transition-colors hover:text-[#0F1817]"
        >
          ← Back
        </button>
      </div>
    </SignupShell>
  );
}
